import './assets/main.css'

import { createApp } from 'vue'
import { createPinia } from 'pinia'

import App from './App.vue'
import router from './router'

import axios from 'axios'
import * as Sentry from '@sentry/vue'

import PrimeVue from 'primevue/config'
import Aura from '@primevue/themes/aura'
import ToastService from 'primevue/toastservice'
import DialogService from 'primevue/dialogservice'
import ConfirmationService from 'primevue/confirmationservice'
import Tooltip from 'primevue/tooltip'
import { definePreset } from '@primevue/themes'

// @ts-ignore
import '@/assets/fontawesome-pro/all.min.js'

const app = createApp(App)

axios.defaults.withCredentials = true
axios.defaults.withXSRFToken = true
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'

app.use(createPinia())
app.use(router)

// FIXME: Because style injection is fucked in PrimeVue at the moment
// we need this hack to force dark mode
// https://github.com/primefaces/primevue/issues/8126
// @ts-ignore-next-line
Aura.semantic.colorScheme.light = Aura.semantic.colorScheme.dark

const CodeCannonPreset = definePreset(Aura, {
	primitive: {
		red: {
			50: '#fef2f2',
			100: '#f9c2c2',
			200: '#f59191',
			300: '#f06161',
			400: '#ec3030',
			500: '#e70000',
			600: '#c40000',
			700: '#a20000',
			800: '#7f0000',
			900: '#5c0000',
		},
		teal: {
			50: '#fdfdfe',
			100: '#f6f5f9',
			200: '#efedf5',
			300: '#e8e5f0',
			400: '#e1ddec',
			500: '#dad5e7',
			600: '#b9b5c4',
			700: '#9995a2',
			800: '#78757f',
			900: '#57555c',
			950: '#37353a',
		},
		yellow: {
			50: '#fefff2',
			100: '#fbffc2',
			200: '#f8ff92',
			300: '#f4ff62',
			400: '#f1ff31',
			500: '#eeff01',
			600: '#cad901',
			700: '#a7b301',
			800: '#838c01',
			900: '#5f6600',
			950: '#3c4000',
		},
		cyan: {
			50: '#fefefe',
			100: '#f9fafc',
			200: '#f4f6f9',
			300: '#f0f1f6',
			400: '#ebedf4',
			500: '#e6e9f1',
			600: '#c4c6cd',
			700: '#a1a3a9',
			800: '#7f8085',
			900: '#5c5d60',
			950: '#3a3a3c',
		},
		blue: {
			0: '#ffffff',
			50: '#f7faff',
			100: '#dae9fd',
			200: '#bcd7fc',
			300: '#9ec5fa',
			400: '#81b4f9',
			500: '#63a2f7',
			600: '#548ad2',
			700: '#4571ad',
			800: '#365988',
			900: '#284163',
			950: '#19293e',
		},
		green: {
			50: '#f4faf6',
			100: '#cae7d2',
			200: '#a1d5af',
			300: '#77c28b',
			400: '#4eb068',
			500: '#249d44',
			600: '#1f853a',
			700: '#196e30',
			800: '#145625',
			900: '#0e3f1b',
			950: '#092711',
		},
	},
	semantic: {
		colorScheme: {
			light: {
				primary: {
					50: '#f2f2f2',
					100: '#c2c2c2',
					200: '#919191',
					300: '#616161',
					400: '#303030',
					500: '#000000',
					600: '#000000',
					700: '#000000',
					800: '#000000',
					900: '#000000',
					950: '#000000',
				},
			},
			dark: {
				primary: {
					0: '#ffffff',
					50: '#ffffff',
					100: '#ffffff',
					200: '#ffffff',
					300: '#ffffff',
					400: '#ffffff',
					500: '#ffffff',
					600: '#d9d9d9',
					700: '#b3b3b3',
					800: '#8c8c8c',
					900: '#666666',
					950: '#404040',
				},
				surface: {
					// 0: '#ffffff',
					// 50: '#f6f5f5',
					// 100: '#d5d0d0',
					// 200: '#b3abab',
					// 300: '#918686',
					// 400: '#706161',
					// 500: '#4e3c3c',
					// 600: '#423333',
					// 700: '#372a2a',
					// 800: '#2b2121',
					// 900: '#1f1818',
					// 950: '#140f0f',
				},
			},
		},
	},
	components: {
		tag: {
			colorScheme: {
				light: {
					primary: {
						background: '{primary.500}',
						color: '#ffffff',
					},
					contrast: {
						background: '{yellow.500}',
						color: '#000000',
					},
					success: {
						background: '{green.500}',
						color: '#ffffff',
					},
					info: {
						background: '{cyan.500}',
						color: '#000000',
					},
				},
				dark: {
					primary: {
						background: '#ffffff',
						color: '#000000',
					},
					contrast: {
						background: '{yellow.500}',
						color: '#000000',
					},
					success: {
						background: '{green.500}',
						color: '#ffffff',
					},
					info: {
						background: '{cyan.500}',
						color: '#000000',
					},
				},
			},
		},
		button: {
			colorScheme: {
				light: {
					primary: {
						background: '{primary.500}',
						color: '#ffffff',
						border: {
							color: '{primary.500}',
						},
						hover: {
							background: '{primary.600}',
							color: '#ffffff',
							border: {
								color: '{primary.600}',
							},
						},
						active: {
							background: '{primary.700}',
							color: '#ffffff',
							border: {
								color: '{primary.700}',
							},
						},
						focus: {
							ring: {
								color: '{primary.500}',
							},
						},
					},
					contrast: {
						background: '{yellow.500}',
						color: '#000000',
						border: {
							color: '{yellow.500}',
						},
						hover: {
							background: '{yellow.600}',
							color: '#000000',
							border: {
								color: '{yellow.600}',
							},
						},
						active: {
							background: '{yellow.700}',
							color: '#000000',
							border: {
								color: '{yellow.700}',
							},
						},
						focus: {
							ring: {
								color: '{yellow.500}',
							},
						},
					},
					success: {
						background: '{green.500}',
						color: '#ffffff',
						border: {
							color: '{green.500}',
						},
						hover: {
							background: '{green.600}',
							color: '#ffffff',
							border: {
								color: '{green.600}',
							},
						},
						active: {
							background: '{green.700}',
							color: '#ffffff',
							border: {
								color: '{green.700}',
							},
						},
						focus: {
							ring: {
								color: '{green.500}',
							},
						},
					},
					info: {
						background: '{cyan.500}',
						color: '#000000',
						border: {
							color: '{cyan.500}',
						},
						hover: {
							background: '{cyan.600}',
							color: '#000000',
							border: {
								color: '{cyan.600}',
							},
						},
						active: {
							background: '{cyan.700}',
							color: '#000000',
							border: {
								color: '{cyan.700}',
							},
						},
						focus: {
							ring: {
								color: '{cyan.500}',
							},
						},
					},
				},
				dark: {
					primary: {
						background: '#ffffff',
						color: '#000000',
						border: {
							color: '#ffffff',
						},
						hover: {
							background: '{primary.50}',
							color: '#000000',
							border: {
								color: '{primary.50}',
							},
						},
						active: {
							background: '{primary.100}',
							color: '#000000',
							border: {
								color: '{primary.100}',
							},
						},
						focus: {
							ring: {
								color: '#ffffff',
							},
						},
					},
					contrast: {
						background: '{yellow.500}',
						color: '#000000',
						border: {
							color: '{yellow.500}',
						},
						hover: {
							background: '{yellow.600}',
							color: '#000000',
							border: {
								color: '{yellow.600}',
							},
						},
						active: {
							background: '{yellow.700}',
							color: '#000000',
							border: {
								color: '{yellow.700}',
							},
						},
						focus: {
							ring: {
								color: '{yellow.500}',
							},
						},
					},
					success: {
						background: '{green.500}',
						color: '#ffffff',
						border: {
							color: '{green.500}',
						},
						hover: {
							background: '{green.600}',
							color: '#ffffff',
							border: {
								color: '{green.600}',
							},
						},
						active: {
							background: '{green.700}',
							color: '#ffffff',
							border: {
								color: '{green.700}',
							},
						},
						focus: {
							ring: {
								color: '{green.500}',
							},
						},
					},
					info: {
						background: '{cyan.500}',
						color: '#000000',
						border: {
							color: '{cyan.500}',
						},
						hover: {
							background: '{cyan.600}',
							color: '#000000',
							border: {
								color: '{cyan.600}',
							},
						},
						active: {
							background: '{cyan.700}',
							color: '#000000',
							border: {
								color: '{cyan.700}',
							},
						},
						focus: {
							ring: {
								color: '{cyan.500}',
							},
						},
					},
				},
			},
		},
	},
})

// @ts-ignore-next-line
CodeCannonPreset.semantic.colorScheme.light = CodeCannonPreset.semantic.colorScheme.dark
// @ts-ignore-next-line
for (const component of Object.keys(CodeCannonPreset.components)) {
	// @ts-ignore-next-line
	if (CodeCannonPreset.components[component].colorScheme)
		// @ts-ignore-next-line
		CodeCannonPreset.components[component].colorScheme.light =
			// @ts-ignore-next-line
			CodeCannonPreset.components[component].colorScheme.dark
}

app.use(PrimeVue, {
	theme: {
		preset: CodeCannonPreset,
		options: {
			darkModeSelector: '.codecannon-dark',
		},
	},
})

app.directive('tooltip', Tooltip)
app.use(ToastService)
app.use(DialogService)
app.use(ConfirmationService)

// @ts-ignore-next-line
if (process.env.NODE_ENV === 'production') {
	Sentry.init({
		app,
		dsn: 'https://253520bf0995368b47769d46ebf4deaf@o4507205539135488.ingest.de.sentry.io/4507205591892048',
		integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
		// Performance Monitoring
		tracesSampleRate: 1.0, //  Capture 100% of the transactions
		// Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
		tracePropagationTargets: ['localhost', /^https:\/\/.*\.?codecannon\.dev\/api/],
		// Session Replay
		replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
		replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
	})
}

app.mount('#app')
